/**
 * État d'ouverture du centre à un instant donné, calculé depuis les horaires
 * saisis dans Strapi (planHoraires.hours : [{ day: 'Lundi', time: '10:00 - 21:30' }]).
 * L'heure utilisée est celle du navigateur (visiteurs en France).
 *
 *   open    -> ouvert
 *   closing -> ouvert, fermeture dans moins d'une heure
 *   closed  -> fermé
 *
 * Renvoie null si les horaires sont absents ou illisibles : l'appelant affiche
 * alors le libellé fixe `site.openLabel`.
 */

const DAYS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi']
const CLOSING_SOON = 60 // minutes

const normalize = (s) =>
  String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase()

// '10:00 - 21:30' (ou '10h00 – 21h30') -> { open, close, openLabel, closeLabel } en minutes
function parseRange(time) {
  const m = /(\d{1,2})[:h](\d{2})\s*[-–à]+\s*(\d{1,2})[:h](\d{2})/i.exec(time || '')
  if (!m) return null
  const open = Number(m[1]) * 60 + Number(m[2])
  let close = Number(m[3]) * 60 + Number(m[4])
  if (close <= open) close += 24 * 60 // fermeture après minuit
  const fmt = (h, min) => `${String(h).padStart(2, '0')}:${min}`
  return { open, close, openLabel: fmt(m[1], m[2]), closeLabel: fmt(m[3], m[4]) }
}

export function openingStatus(hours, now = new Date()) {
  if (!Array.isArray(hours) || !hours.length) return null

  const byDay = {}
  for (const h of hours) byDay[normalize(h.day)] = parseRange(h.time)
  // aucun jour lisible : on ne sait rien dire de fiable
  if (!Object.values(byDay).some(Boolean)) return null

  const dayIdx = now.getDay()
  const minutes = now.getHours() * 60 + now.getMinutes()
  const today = byDay[DAYS[dayIdx]]

  if (today && minutes >= today.open && minutes < today.close) {
    if (today.close - minutes <= CLOSING_SOON) {
      return { state: 'closing', label: `Fermeture à ${today.closeLabel}` }
    }
    return { state: 'open', label: `Ouvert de ${today.openLabel} à ${today.closeLabel}` }
  }

  // fermé : prochaine ouverture
  if (today && minutes < today.open) {
    return { state: 'closed', label: `Fermé · ouvre à ${today.openLabel}` }
  }
  for (let i = 1; i <= 7; i++) {
    const next = byDay[DAYS[(dayIdx + i) % 7]]
    if (next) {
      const when = i === 1 ? 'demain' : DAYS[(dayIdx + i) % 7]
      return { state: 'closed', label: `Fermé · ouvre ${when} à ${next.openLabel}` }
    }
  }
  return { state: 'closed', label: 'Fermé' }
}
